import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { green } from "@mui/material/colors";
import { isEqual } from "lodash";
import { FS } from "@plannel/utils/font-util";

/*
 * Measure 설정 그리드(DP Settings > Workbench Measure)용 셀 렌더러 모음.
 *
 * 값·편집 로직은 그대로 두고 "보이는 것"만 바꾼다 —
 * 체크박스 → 스위치, compute 체크박스 → ƒ 배지, 타입 → 칩, 빈 칸 → 회색 블록 대신 "—".
 * 모두 AG Grid cellRenderer 시그니처(params)를 따른다.
 */

// colDef.editable(boolean | fn)을 AG Grid 와 같은 규칙으로 평가한다(비활성 셀을 흐리게 표시하는 용도).
export const isCellEditable = params => {
  const { editable } = params?.colDef ?? {};
  if (!editable) return false;
  return (typeof editable === "function" && editable(params)) || isEqual(editable, true);
};

const EMPTY_MARK = "—";

const emptyText = value => value === null || value === undefined || value === "";

/*
 * ON — 켜짐/꺼짐 스위치(보기 전용).
 * 토글은 colDef 의 booleanColumn onCellClicked 가 이미 처리하므로 여기서 onClick 을 달지 않는다(이중 토글 방지).
 */
export const MeasureActiveCell = params => {
  if (params.node?.rowPinned) return null;

  const on = !!params.value;
  const editable = isCellEditable(params);

  return (
    <Box
      sx={{
        display: "flex", alignItems: "center", height: "100%",
        opacity: editable ? 1 : 0.45,
      }}
    >
      <Box
        sx={{
          position: "relative", width: 22, height: 12, borderRadius: 999,
          bgcolor: on ? "primary.main" : "grey.400",
          transition: "background-color .15s ease",
          "&::after": {
            content: '""', position: "absolute", top: 2, left: on ? 12 : 2,
            width: 8, height: 8, borderRadius: "50%", bgcolor: "#fff",
            transition: "left .15s ease",
          },
        }}
      />
    </Box>
  );
};

/*
 * FX — 계산식 measure 여부를 ƒ 배지로. 켜져 있으면 teal 채움, 아니면 옅은 외곽선.
 * 토글 핸들러는 cellRendererParams.onToggle 로 받는다(기존 checkboxClickEvent 를 그대로 넘긴다).
 */
export const MeasureComputeCell = params => {
  if (params.node?.rowPinned) return null;

  const on = !!params.value;
  const editable = isCellEditable(params);

  return (
    <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
      <Box
        component="span"
        onClick={e => {
          if (!editable) return;
          params.onToggle?.(e, params);
        }}
        sx={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 16, height: 16, borderRadius: 0.75,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: FS.BODY, fontStyle: "italic", lineHeight: 1,
          cursor: editable ? "pointer" : "default",
          bgcolor: on ? "primary.main" : "transparent",
          color: on ? "primary.contrastText" : "text.disabled",
          border: 1, borderColor: on ? "primary.main" : "divider",
          opacity: editable || on ? 1 : 0.5,
        }}
      >
        f
      </Box>
    </Box>
  );
};

// TYPE — AMT/QTY 를 옅은 teal 칩으로.
export const MeasureTypeCell = params => {
  const text = params.valueFormatted ?? params.value;
  if (emptyText(text)) return <MeasureTextCell {...params} />;

  return (
    <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
      <Box
        component="span"
        sx={{
          px: 0.75, height: 16, borderRadius: 0.75,
          display: "inline-flex", alignItems: "center",
          fontSize: FS.CAPTION, fontWeight: 600, letterSpacing: ".04em",
          bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
          color: "primary.dark",
        }}
      >
        {text}
      </Box>
    </Box>
  );
};

/*
 * 일반 텍스트 — 값이 없으면 회색 블록 대신 "—" 를 흐리게 찍는다.
 * cellRendererParams 로 톤을 고른다: mono(수식), placeholder(빈 값에 보여줄 안내 문구).
 */
export const MeasureTextCell = params => {
  const text = params.valueFormatted ?? params.value;
  const isEmpty = emptyText(text);
  const placeholder = params.placeholder;

  return (
    <Box
      component="span"
      title={isEmpty ? "" : String(text)}
      sx={{
        display: "block", width: "100%",
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        color: isEmpty ? "text.disabled" : params.mono ? "primary.dark" : "text.primary",
        fontFamily: params.mono && !isEmpty ? "ui-monospace, SFMono-Regular, Menlo, monospace" : undefined,
        fontSize: params.mono && !isEmpty ? FS.BODY : undefined,
      }}
    >
      {isEmpty ? (placeholder ?? EMPTY_MARK) : text}
    </Box>
  );
};

/*
 * 목록형(list + panel) 레이아웃의 한 줄 — 이름 + ƒ + 타입 칩 + 설정 요약.
 * 요약 문자열은 화면마다 다르므로 cellRendererParams.summary(data) 가 만들어 넘긴다.
 *   summary: data => ({ name, fx: boolean, type: string, meta: string })
 */
export const MeasureListCell = params => {
  const { name, fx, type, user, original, meta } = params.summary?.(params.data) ?? {};

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, height: "100%", minWidth: 0 }}>
      <Box
        component="span"
        sx={{
          fontSize: FS.SUB, color: "text.primary", flexShrink: 0, maxWidth: "45%",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}
      >
        {name}
      </Box>
      {fx && (
        <Box
          component="span"
          sx={{
            flexShrink: 0, width: 14, height: 14, borderRadius: 0.5,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: FS.CAPTION, fontStyle: "italic", lineHeight: 1,
            bgcolor: "primary.main", color: "primary.contrastText",
          }}
        >
          f
        </Box>
      )}
      {/*
        * 칩·요약 모두 measure 이름과 같은 글자 크기(12px)로 둔다 —
        * 한 줄 안에서 글자 크기가 세 가지로 갈리면 목록이 시끄러워진다.
        * 구분은 크기가 아니라 색(칩=teal 틴트, 요약=회색)이 한다.
        */}
      {type && (
        <Box
          component="span"
          sx={{
            flexShrink: 0, px: 0.6, height: 18, borderRadius: 0.5,
            display: "inline-flex", alignItems: "center",
            fontSize: FS.SUB,
            bgcolor: theme => alpha(theme.palette.primary.main, 0.1), color: "primary.dark",
          }}
        >
          {type}
        </Box>
      )}
      {/* 오른쪽 — 사용자 measure 표시, 이름을 바꿨으면 원래 이름, 그리고 설정 요약 */}
      <Box sx={{ ml: "auto", pl: 1, display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
        {user && (
          /* 사용자가 만든 measure — 타입 칩(teal)과 헷갈리지 않게 옅은 초록으로 */
          <Box
            component="span"
            sx={{
              flexShrink: 0, px: 0.6, height: 18, borderRadius: 0.5,
              display: "inline-flex", alignItems: "center",
              fontSize: FS.SUB, bgcolor: green[50], color: green[800],
            }}
          >
            {params.userLabel}
          </Box>
        )}
        {original && (
          <Box
            component="span"
            title={original}
            sx={{
              flexShrink: 1, minWidth: 0, fontSize: FS.SUB, fontStyle: "italic", color: "text.disabled",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}
          >
            ({original})
          </Box>
        )}
        <Box
          component="span"
          sx={{
            fontSize: FS.SUB, color: "text.disabled",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}
        >
          {meta}
        </Box>
      </Box>
    </Box>
  );
};

export default MeasureTextCell;
